import { SampleTypeEnum } from './sampleType.enum';
import { SampleCategoryEnum } from './sampleCategory.enum';

export enum SampleStatusEnum {
    Planned = 0,
    Draft = 1,
    Ongoing = 2,
    AwaitingPostSampleInfo = 9,
    AwaitingLabSubmission = 3,
    SubmittedToLab = 4,
    ReceivedByLab = 5,
    AwaitingApproval = 6,
    Approved = 7,
    ApprovedLetterSent = 10,
    Void = 8,
    VoidLetterSent = 11,
}

export const SampleStatusColors: Record<SampleStatusEnum, string> = {
    [SampleStatusEnum.Planned]: '#F1F5F9',
    [SampleStatusEnum.Draft]: '#FEF3C7',
    [SampleStatusEnum.Ongoing]: '#E0F2FE',
    [SampleStatusEnum.AwaitingPostSampleInfo]: '#FFEDD5',
    [SampleStatusEnum.AwaitingLabSubmission]: '#FED7AA',
    [SampleStatusEnum.SubmittedToLab]: '#EDE9FE',
    [SampleStatusEnum.ReceivedByLab]: '#DDD6FE',
    [SampleStatusEnum.AwaitingApproval]: '#DBEAFE',
    [SampleStatusEnum.Approved]: '#DCFCE7',
    [SampleStatusEnum.ApprovedLetterSent]: '#BBF7D0',
    [SampleStatusEnum.Void]: '#FEE2E2',
    [SampleStatusEnum.VoidLetterSent]: '#FECACA',
};

export const SampleStatusTextColors: Record<SampleStatusEnum, string> = {
    [SampleStatusEnum.Planned]: '#334155',
    [SampleStatusEnum.Draft]: '#92400E',
    [SampleStatusEnum.Ongoing]: '#075985',
    [SampleStatusEnum.AwaitingPostSampleInfo]: '#9A3412',
    [SampleStatusEnum.AwaitingLabSubmission]: '#7C2D12',
    [SampleStatusEnum.SubmittedToLab]: '#5B21B6',
    [SampleStatusEnum.ReceivedByLab]: '#4C1D95',
    [SampleStatusEnum.AwaitingApproval]: '#1E40AF',
    [SampleStatusEnum.Approved]: '#166534',
    [SampleStatusEnum.ApprovedLetterSent]: '#14532D',
    [SampleStatusEnum.Void]: '#991B1B',
    [SampleStatusEnum.VoidLetterSent]: '#7F1D1D',
};

export function getStatusBadgeClass(statusId: number): string {
    const name = SampleStatusEnum[statusId];
    if (!name) return 'status-badge--blank';
    return 'status-badge--' + name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

export const SampleEditableFields = {
    fullEditable: [SampleStatusEnum.Planned, SampleStatusEnum.Draft, SampleStatusEnum.Ongoing],
    immutableIDs: [SampleStatusEnum.AwaitingLabSubmission, SampleStatusEnum.SubmittedToLab, SampleStatusEnum.ReceivedByLab, SampleStatusEnum.AwaitingApproval],
    nonEditable: [SampleStatusEnum.Approved, SampleStatusEnum.ApprovedLetterSent, SampleStatusEnum.Void, SampleStatusEnum.VoidLetterSent]
};

/**
 * Returns the list of sample statuses filtered by sample type and category.
 * Rules:
 *  - All types: remove Planned (not used)
 *  - Noise: remove SubmittedToLab, ReceivedByLab; rename AwaitingLabSubmission → "Awaiting Results"
 *  - Wipe: remove Ongoing
 *  - Blank (category): remove Ongoing
 *
 * @param allStatuses - full status lookup list (from getEnumLookup(SampleStatusEnum))
 * @param sampleType - SampleTypeEnum value (0=Air, 1=Noise, 2=Wipe)
 * @param sampleCategory - SampleCategoryEnum value (0=Personal, 1=Area, 2=Blank)
 */
export function getFilteredSampleStatuses(
  allStatuses: { id: number; name: string }[],
  sampleType?: number | null,
  sampleCategory?: number | null,
  currentStatusId?: number | null
): { id: number; name: string }[] {
  // All sample types: remove Planned
  let filtered = allStatuses.filter(s => s.id !== SampleStatusEnum.Planned);

  if (sampleType === SampleTypeEnum.Noise) {
    filtered = filtered.filter(s =>
      s.id !== SampleStatusEnum.SubmittedToLab && s.id !== SampleStatusEnum.ReceivedByLab
    );
    filtered = filtered.map(s =>
      s.id === SampleStatusEnum.AwaitingLabSubmission ? { ...s, name: 'Awaiting Results' } : s
    );
  }

  if (sampleType === SampleTypeEnum.Wipe) {
    filtered = filtered.filter(s => s.id !== SampleStatusEnum.Ongoing);
  }

  if (sampleCategory === SampleCategoryEnum.Blank) {
    filtered = filtered.filter(s => s.id !== SampleStatusEnum.Ongoing);
  }

  // Preserve the currently persisted status so the UI control never goes blank
  if (currentStatusId != null && !filtered.some(s => s.id === currentStatusId)) {
    const missing = allStatuses.find(s => s.id === currentStatusId);
    if (missing) {
      filtered = [missing, ...filtered];
    }
  }

  return filtered;
}

/**
 * Logical ordering of sample statuses for transition validation.
 * Enum numeric values are non-sequential, so we define explicit ordering.
 */
const STATUS_ORDER: Record<SampleStatusEnum, number> = {
    [SampleStatusEnum.Planned]: 0,
    [SampleStatusEnum.Draft]: 1,
    [SampleStatusEnum.Ongoing]: 2,
    [SampleStatusEnum.AwaitingPostSampleInfo]: 3,
    [SampleStatusEnum.AwaitingLabSubmission]: 4,
    [SampleStatusEnum.SubmittedToLab]: 5,
    [SampleStatusEnum.ReceivedByLab]: 6,
    [SampleStatusEnum.AwaitingApproval]: 7,
    [SampleStatusEnum.Approved]: 8,
    [SampleStatusEnum.ApprovedLetterSent]: 9,
    [SampleStatusEnum.Void]: 10,
    [SampleStatusEnum.VoidLetterSent]: 11,
};

/**
 * A sample is considered "locked" once it reaches AwaitingLabSubmission or later
 * in the logical status ordering (mirrors the LOCKED_THRESHOLD used for transition validation).
 */
export function isLockedSampleStatus(status: SampleStatusEnum): boolean {
    const order = STATUS_ORDER[status];
    if (order == null) return false;
    return order >= STATUS_ORDER[SampleStatusEnum.AwaitingLabSubmission];
}

export function isSampleStatusSelectable(current: SampleStatusEnum, target: SampleStatusEnum): boolean {
    if (target === current) return true;

    const LOCKED_THRESHOLD = STATUS_ORDER[SampleStatusEnum.AwaitingLabSubmission];
    const currentIsLocked = STATUS_ORDER[current] >= LOCKED_THRESHOLD;
    const targetIsLocked = STATUS_ORDER[target] >= LOCKED_THRESHOLD;

    // The only restriction: cannot transition from a locked status back to an unlocked status
    if (currentIsLocked && !targetIsLocked) return false;

    return true;
}